/* eslint-disable max-lines-per-function */
import { Icon20Cancel, Icon20ChevronUpOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ENABLE_FULL_RESPONSIVE } from 'reactApp/appHelpers/configHelpers';
import { ANTIVIRUS_BRAND } from 'reactApp/appHelpers/featuresHelpers/features/antivirusBrand';
import { cloudFilesMigrationModal } from 'reactApp/appHelpers/featuresHelpers/features/cloudFilesMigrationDesktop';
import {
    closeFilesMigrationStatusKey,
    CLOUD_FILES_MIGRATION_PAY_SOURCE,
    filesMigrationFolderId,
    filesMigrationFolderName,
} from 'reactApp/modules/filesMigration/filesMigration.constants';
import { cancelFilesMigrationRequest, changeStatusVisibility } from 'reactApp/modules/filesMigration/filesMigration.module';
import {
    getAllChatsCount,
    getFilesMigrationStatus,
    getIsLoading,
    getIsStatusHide,
    getMigratedBytes,
    getMigratedChatsCount,
    hasActiveMigration,
    hasWaitForMigration,
} from 'reactApp/modules/filesMigration/filesMigration.selectors';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { useTariffBuy } from 'reactApp/modules/payment/hooks/useTariffBuy';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { historyPush } from 'reactApp/modules/router/router.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { safetyPersonalLocalStorage } from 'reactApp/storage';
import type { RootState } from 'reactApp/store';
import { FileType } from 'reactApp/types/FileType';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { getInfoblockContent, getTexts } from 'reactApp/ui/FilesMigrationStatus/FilesMigrationStatus.helpers';
import { InfoComponent } from 'reactApp/ui/FilesMigrationStatus/InfoComponent/InfoComponent';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { ProtectedHint } from 'reactApp/ui/ProtectedHint/ProtectedHint';
import { LoaderIcon } from 'reactApp/ui/UploadList/Item/LoaderIcon';
import { getSize } from 'reactApp/utils/sizeHelper';
import { getHomeFolderPath } from 'reactApp/utils/urlHelper';

import styles from './FilesMigrationStatus.css';

const hintAntivirusText = `Файлы проверяются антивирусом${ANTIVIRUS_BRAND === 'default' ? '' : ` ${ANTIVIRUS_BRAND}`}`;

export const FilesMigrationStatus = memo(() => {
    const dispatch = useDispatch();

    const isStatusHide = useSelector(getIsStatusHide);

    const [isCollapsed, setIsCollapsed] = useState(false);

    const isB2BUser = useSelector(UserSelectors.isB2BUser);

    const isCancelLoading = useSelector(getIsLoading);
    const allChatsCount = useSelector(getAllChatsCount);
    const migratedChatsCount = useSelector(getMigratedChatsCount);
    const migratedBytes = useSelector(getMigratedBytes);
    const status = useSelector(getFilesMigrationStatus);
    const telegramFolderSize = useSelector((state) => getHomeItemById(state as RootState, filesMigrationFolderId))?.size;

    const { title, description } = getTexts(status, { allChatsCount, migratedChatsCount, migratedBytes });
    const onBuyClick = useTariffBuy({
        paySource: getPaySource(CLOUD_FILES_MIGRATION_PAY_SOURCE),
    });

    const openBuyModal = () =>
        dispatch(
            openBuyDisableAds({
                title: cloudFilesMigrationModal.title,
                tariffsNumber: cloudFilesMigrationModal.tarrifsNumber,
                activeTab: cloudFilesMigrationModal.activeTab,
                onBuyClick,
                isFrame: false,
                paySource: getPaySource(CLOUD_FILES_MIGRATION_PAY_SOURCE),
            })
        );

    const infoblockStatusContent = getInfoblockContent(status, openBuyModal, { isB2BUser });

    const progress = allChatsCount > 0 ? Math.round((migratedChatsCount / allChatsCount) * 100) : 0;

    const handleExpandClick = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleCloseClick = () => {
        dispatch(changeStatusVisibility({ closed: true }));
        safetyPersonalLocalStorage.setItem(closeFilesMigrationStatusKey, 'closed');
    };

    const handleCancelClick = () => {
        dispatch(cancelFilesMigrationRequest());
    };

    const handleFolderClick = () => {
        dispatch(historyPush({ id: getHomeFolderPath(filesMigrationFolderId) }));
    };

    const isUploading = useSelector(hasActiveMigration);
    const isWaitingForMigration = useSelector(hasWaitForMigration);

    const isHideCloseButton = isUploading || isWaitingForMigration;

    if (isStatusHide) {
        return null;
    }

    return (
        <div
            className={classNames(styles.root, {
                [styles.responsive]: ENABLE_FULL_RESPONSIVE,
            })}
            data-test-id={`tgmig-status-root-${status}`}
        >
            <div
                className={classNames(styles.header, {
                    [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                    [styles.header_noInfo]: !infoblockStatusContent,
                })}
            >
                <div
                    className={classNames(styles.controlsWrapper, {
                        [styles.controlsWrapper_Info]: Boolean(infoblockStatusContent),
                    })}
                >
                    <div className={styles.textWrapper}>
                        <div
                            className={classNames(styles.mainText, {
                                [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                            })}
                        >
                            <span data-test-id="tgmig-status-title">{title}</span>
                            {!isB2BUser && <ProtectedHint text={hintAntivirusText} hover noMargin />}
                        </div>
                        <div className={styles.secondaryText} data-test-id="tgmig-status-description">
                            {description}
                        </div>
                    </div>
                    <div className={styles.controls}>
                        <Hint text={isCollapsed ? 'Развернуть' : 'Свернуть'}>
                            <div
                                className={classNames(styles.expand, {
                                    [styles.expandClosed]: !isCollapsed,
                                })}
                                onClick={handleExpandClick}
                                data-test-id="tgmig-status-expand"
                            >
                                <Icon20ChevronUpOutline />
                            </div>
                        </Hint>
                        {!isHideCloseButton && (
                            <Hint text="Закрыть">
                                <div className={styles.close} onClick={handleCloseClick} data-test-id="tgmig-status-close">
                                    <Icon20Cancel />
                                </div>
                            </Hint>
                        )}
                    </div>
                </div>
            </div>
            {infoblockStatusContent && <InfoComponent {...infoblockStatusContent} />}
            <div
                className={classNames(styles.files, {
                    [styles.hide]: isCollapsed,
                })}
            >
                <div className={styles.fileItem}>
                    <div
                        className={classNames(styles.fileInfo, {
                            [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                        })}
                        onClick={handleFolderClick}
                        data-test-id="tgmig-status-folder"
                    >
                        <div className={styles.fileIcon}>
                            <FileIcon type={FileType.FOLDER_TELEGRAM} />
                        </div>
                        <div>
                            <div className={styles.fileName}>
                                <NameComponent name={filesMigrationFolderName} truncate />
                            </div>
                            {telegramFolderSize && getSize(telegramFolderSize) !== '0' ? (
                                <div className={styles.fileSize}>{getSize(telegramFolderSize)}</div>
                            ) : null}
                        </div>
                    </div>
                    {isUploading && (
                        <div className={styles.loadingIconWrap} onClick={handleCancelClick}>
                            <LoaderIcon progress={progress} loading={!isCancelLoading} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

FilesMigrationStatus.displayName = 'FilesMigrationStatus';
